/**
 * OverviewTab — primary contact + decision-maker + social + follow-up.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Globe, Tag, Calendar, Loader2, Sliders, Youtube, Save, Search, ExternalLink, Star } from "lucide-react";
import AddressCard from "./AddressCard";

const YELP_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_BADGE = {
  valid:   { pt: "Verificado", cls: "bg-emerald-500/15 text-emerald-500" },
  risky:   { pt: "Genérico", cls: "bg-amber-500/15 text-amber-500" },
  invalid: { pt: "Inválido", cls: "bg-red-500/15 text-red-400" },
};

function EmailBadge({ lead, onChanged, isPt }) {
  const [busy, setBusy] = useState(false);
  const st = lead.email_status;
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${YELP_API}/email/leads/${lead.id}/verify`);
      if (data.ok) {
        toast.success(isPt ? `Email ${EMAIL_BADGE[data.status]?.pt || data.status}` : `Email ${data.status}`);
        onChanged?.();
      } else toast.warning(isPt ? "Lead sem email" : "No email on lead");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "verify failed");
    } finally { setBusy(false); }
  };
  if (!lead.email) return null;
  return st ? (
    <span data-testid="ldd-email-badge" title={lead.email_mx_ok ? "MX OK" : "Sem servidor de email (MX)"}
      className={`inline-flex items-center gap-1 normal-case tracking-normal text-[9.5px] font-bold px-1.5 py-0.5 rounded ${(EMAIL_BADGE[st] || {}).cls || "bg-gray-500/15 text-gray-400"}`}>
      {st === "valid" ? "✓" : st === "invalid" ? "✕" : "!"} {(EMAIL_BADGE[st] || {}).pt || st}
    </span>
  ) : (
    <button type="button" data-testid="ldd-email-verify-btn" onClick={verify} disabled={busy}
      className="inline-flex items-center gap-1 normal-case tracking-normal text-[9.5px] font-bold px-1.5 py-0.5 rounded border border-[var(--al-border)] text-[var(--al-text-muted)] hover:text-[#3B82F6] disabled:opacity-60">
      {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : null}
      {isPt ? "Verificar email" : "Verify email"}
    </button>
  );
}

function YelpChip({ lead, onChanged, isPt }) {
  const [busy, setBusy] = useState(false);
  const enrich = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${YELP_API}/yelp/enrich/${lead.id}`);
      if (data.ok) {
        toast.success(isPt ? "Lead enriquecido com dados do Yelp" : "Enriched with Yelp data");
        onChanged?.();
      } else {
        toast.warning(isPt ? "Nenhum match confiável no Yelp" : "No confident Yelp match");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yelp enrich failed");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="ldd-yelp-chip" className="flex items-center gap-2 flex-wrap">
      {lead.yelp_rating != null ? (
        <a href={lead.yelp_url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#D32323]/10 text-[#D32323] text-[11px] font-bold hover:underline">
          <Star className="w-3 h-3 fill-[#D32323]" />
          Yelp {lead.yelp_rating} ({lead.yelp_review_count} reviews)
        </a>
      ) : (
        <button type="button" data-testid="ldd-yelp-enrich-btn" onClick={enrich} disabled={busy}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-[var(--al-border)] text-[10.5px] font-semibold text-[var(--al-text-muted)] hover:text-[#D32323] hover:border-[#D32323]/50 disabled:opacity-60">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
          {isPt ? "Enriquecer com Yelp" : "Enrich with Yelp"}
        </button>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[var(--al-text-muted)] text-[11px] uppercase tracking-wide mb-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-[var(--al-text)] break-words">{value}</div>
    </div>
  );
}

export default function OverviewTab({
  lead, t, phoneRaw,
  enriching, enrichDecisionMaker,
  followup, setFollowup, savingFollowup, saveFollowup, clearFollowup,
  // Iter113 — manual phone/email patch for any lead (Sunbiz uses
  // /api/sunbiz/leads/{doc}/contact, others use PATCH /api/leads/{id}).
  savingContact, saveContact,
  onChanged,
}) {
  const [phoneInput, setPhoneInput] = useState(phoneRaw || "");
  const [emailInput, setEmailInput] = useState(lead.email || "");
  const isPt = (typeof window !== "undefined" && window.localStorage.getItem("alp.lang")) !== "en";
  const phoneLooksValid = /^\+?[\d\s().-]{10,}$/.test((phoneInput || "").trim());

  // Keep local inputs in sync when the drawer reloads with a different lead.
  useEffect(() => { setPhoneInput(phoneRaw || ""); }, [lead.id, phoneRaw]);
  useEffect(() => { setEmailInput(lead.email || ""); }, [lead.id, lead.email]);

  const onSaveContact = () => {
    saveContact?.({
      phone: phoneInput.trim(),
      email: emailInput.trim(),
    });
  };

  const phoneChanged = (phoneInput || "") !== (phoneRaw || "");
  const emailChanged = (emailInput || "") !== (lead.email || "");
  const dirty = phoneChanged || emailChanged;

  return (
    <>
      {/* Iter113 — manual contact editor (always editable, replaces the
          read-only fields that used to render here). */}
      <div className="rounded-lg border border-[var(--al-border)] bg-[var(--al-row-alt)] p-3 space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold">
            Contato
          </span>
          {dirty && (
            <span className="text-[10px] font-bold text-[#F97316]">
              Não salvo
            </span>
          )}
        </div>
        <div>
          <label className="flex items-center gap-1.5 text-[var(--al-text-muted)] text-[10.5px] uppercase tracking-wide mb-1">
            <Phone className="w-3 h-3" /> {t("ldd.field_phone")}
            {(lead.whatsapp_available || phoneLooksValid) && phoneInput && (
              <span data-testid="ldd-wa-available" className="inline-flex items-center gap-1 normal-case tracking-normal text-[9.5px] font-bold text-[#25D366]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#25D366]" />
                {isPt ? "WhatsApp disponível" : "WhatsApp available"}
              </span>
            )}
          </label>
          <input
            type="tel"
            data-testid="ldd-phone-input"
            value={phoneInput}
            placeholder="+1 (555) 123-4567"
            onChange={(e) => setPhoneInput(e.target.value)}
            className="al-input h-8 text-[12.5px] w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-1.5 text-[var(--al-text-muted)] text-[10.5px] uppercase tracking-wide mb-1">
            <Mail className="w-3 h-3" /> {t("ldd.field_email")}
            <EmailBadge lead={lead} onChanged={onChanged} isPt={isPt} />
          </label>
          <input
            type="email"
            data-testid="ldd-email-input"
            value={emailInput}
            placeholder="contato@empresa.com"
            onChange={(e) => setEmailInput(e.target.value)}
            className="al-input h-8 text-[12.5px] w-full"
          />
        </div>
        <div className="flex items-center justify-end">
          <button
            type="button"
            data-testid="ldd-contact-save"
            onClick={onSaveContact}
            disabled={savingContact || !dirty}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {savingContact
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Save className="w-3.5 h-3.5" />}
            Salvar contato
          </button>
        </div>
      </div>

      {lead.website && (
        <Field icon={Globe} label={t("ldd.field_website")}
          value={
            <a href={lead.website} target="_blank" rel="noopener noreferrer"
              className="text-[#3B82F6] hover:underline break-all">
              {lead.website.replace(/^https?:\/\//, "")}
            </a>
          } />
      )}
      <AddressCard lead={lead} onChanged={onChanged} isPt={isPt} />
      <YelpChip lead={lead} onChanged={onChanged} isPt={isPt} />
      {lead.sector && <Field icon={Tag} label={t("ldd.field_sector")} value={lead.sector} />}

      {/* Decision-maker block */}
      <div className="rounded-lg border border-[var(--al-border)] bg-[var(--al-row-alt)] p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold">Decision-maker</span>
          <button type="button" data-testid="ldd-enrich-dm" onClick={enrichDecisionMaker} disabled={enriching}
            className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-1 rounded bg-[#1B3A8A] hover:bg-[#234aac] text-white disabled:opacity-60">
            {enriching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sliders className="w-3 h-3" />}
            {enriching ? "..." : "AI Enrich"}
          </button>
        </div>
        {lead.decision_maker?.name ? (
          <div className="text-[12.5px]">
            <div className="font-semibold text-[var(--al-text)]">{lead.decision_maker.name}</div>
            {lead.decision_maker.role && <div className="text-[var(--al-text-muted)]">{lead.decision_maker.role}</div>}
            {lead.decision_maker.confidence !== undefined && (
              <div className="text-[10.5px] text-[var(--al-text-muted)] mt-0.5">Confidence: {lead.decision_maker.confidence}%</div>
            )}
          </div>
        ) : (
          <div className="text-[11.5px] italic text-[var(--al-text-muted)]">No decision-maker yet — click <strong>AI Enrich</strong>.</div>
        )}
      </div>

      {/* Iter115 — External web-search shortcuts for finding owner phone +
          public records. We DO NOT scrape these sites (Cloudflare/CAPTCHA
          would block our server). Instead we deep-link the user into the
          right search URL and they paste the result back via the manual
          phone/email inputs above. Two seconds per lead vs. zero. */}
      <ExternalLookups lead={lead} />

      {/* Social links (Instagram / LinkedIn / Facebook / YouTube) */}
      {(lead.linkedin_url || lead.instagram_url || lead.facebook_url || lead.youtube_url) && (
        <div className="flex items-center gap-2 pt-1 flex-wrap">
          <span className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mr-1">Social</span>
          {lead.linkedin_url && (
            <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" data-testid="ldd-linkedin"
              className="px-2.5 py-1 rounded-md bg-[#0A66C2]/10 text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white text-[11px] font-semibold inline-flex items-center gap-1">LinkedIn</a>
          )}
          {lead.instagram_url && (
            <a href={lead.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="ldd-instagram"
              className="px-2.5 py-1 rounded-md bg-[#E1306C]/10 text-[#E1306C] hover:bg-[#E1306C] hover:text-white text-[11px] font-semibold inline-flex items-center gap-1">Instagram</a>
          )}
          {lead.facebook_url && (
            <a href={lead.facebook_url} target="_blank" rel="noopener noreferrer" data-testid="ldd-facebook"
              className="px-2.5 py-1 rounded-md bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2] hover:text-white text-[11px] font-semibold inline-flex items-center gap-1">Facebook</a>
          )}
          {lead.youtube_url && (
            <a href={lead.youtube_url} target="_blank" rel="noopener noreferrer" data-testid="ldd-youtube"
              className="px-2.5 py-1 rounded-md bg-[#FF0000]/10 text-[#FF0000] hover:bg-[#FF0000] hover:text-white text-[11px] font-semibold inline-flex items-center gap-1">
              <Youtube className="w-3 h-3" /> YouTube
            </a>
          )}
          {lead.source && /scraper/i.test(String(lead.source)) && (
            <span
              data-testid="ldd-source-scraper"
              title={`Lead imported from ${lead.source}`}
              className="px-2 py-0.5 rounded bg-[#F3F4F6] text-[#6B7280] text-[10px] font-bold uppercase tracking-wider"
            >
              {String(lead.source).replace("_scraper", "")}
            </span>
          )}
        </div>
      )}

      {/* Follow-up */}
      <div>
        <div className="flex items-center gap-1.5 text-[var(--al-text-muted)] text-[11px] uppercase tracking-wide mb-1.5">
          <Calendar className="w-3 h-3" /> {t("ldd.field_followup")}
        </div>
        <div className="flex items-center gap-2">
          <input type="date" data-testid="ldd-followup-input" value={followup}
            onChange={(e) => setFollowup(e.target.value)}
            className="al-input h-8 text-[12.5px] flex-1" />
          <button type="button" data-testid="ldd-followup-save" onClick={saveFollowup} disabled={savingFollowup}
            className="h-8 px-3 rounded-lg bg-[#3B82F6] text-white text-[12px] font-semibold hover:bg-[#2563EB] disabled:opacity-50">
            {savingFollowup ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t("ldd.followup_save")}
          </button>
          {lead.next_followup_at && (
            <button type="button" data-testid="ldd-followup-clear" onClick={clearFollowup}
              className="h-8 px-2 rounded-lg border border-[var(--al-border)] text-[12px] text-[var(--al-text-muted)] hover:text-[#DC2626]">
              {t("ldd.followup_clear")}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * ExternalLookups — UI-only deep-links to public people-search and
 * business-registry sites. Pre-fills the search query with the lead's
 * owner name (or company name) + city/state when available, so the
 * operator finds a phone number in 2 clicks and pastes it into the
 * manual phone/email inputs above.
 *
 * NEVER fetch from these endpoints server-side — they sit behind
 * Cloudflare and will ban the IP.
 */
function ExternalLookups({ lead }) {
  // Pick the best "person" we know about for people-search engines.
  const ownerName =
    lead?.decision_maker?.name ||
    lead?.sunbiz?.officer_name ||
    lead?.sunbiz?.registered_agent ||
    "";
  const company = lead?.name || lead?.sunbiz?.company_name || "";

  // City + state extraction — Sunbiz leads have explicit fields, regular
  // leads only carry a free-form address. We try to be helpful, not perfect.
  const city =
    lead?.sunbiz?.principal_city ||
    (lead?.address || "").split(",")[1]?.trim() ||
    "";
  const state =
    lead?.sunbiz?.principal_state || "FL";          // sunbiz default

  // URL builders. The sites accept these exact query patterns as of 2026.
  const cityState = [city, state].filter(Boolean).join(" ").trim();
  const tps = ownerName
    ? `https://www.truepeoplesearch.com/results?name=${encodeURIComponent(ownerName)}${
        cityState ? `&citystatezip=${encodeURIComponent(cityState)}` : ""
      }`
    : null;
  const fps = ownerName
    ? `https://www.fastpeoplesearch.com/name/${encodeURIComponent(
        ownerName.toLowerCase().replace(/\s+/g, "-")
      )}${cityState ? `_${encodeURIComponent(cityState.replace(/\s+/g, "-").toLowerCase())}` : ""}`
    : null;
  const biza = company
    ? `https://www.bizapedia.com/search?q=${encodeURIComponent(company)}`
    : null;
  // Iter116 — Google Maps deep-link (reviews + public phone listed
  // directly on the business profile, often more reliable than a
  // people-search hit).
  const gmaps = company
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [company, city, state].filter(Boolean).join(" ")
      )}`
    : null;

  // Nothing to deep-link to → render nothing (no empty card).
  if (!tps && !fps && !biza && !gmaps) return null;

  return (
    <div className="rounded-lg border border-[var(--al-border)] bg-[var(--al-row-alt)] p-3 space-y-2">
      <div className="flex items-center gap-1.5">
        <Search className="w-3 h-3 text-[var(--al-text-muted)]" />
        <span className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold">
          Buscar contato na web
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tps && (
          <a
            href={tps}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ldd-lookup-truepeoplesearch"
            title={`Buscar "${ownerName}" no TruePeopleSearch`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#3B82F6]/10 hover:bg-[#3B82F6] text-[#3B82F6] hover:text-white text-[11px] font-semibold transition"
          >
            <ExternalLink className="w-2.5 h-2.5" /> TruePeopleSearch
          </a>
        )}
        {fps && (
          <a
            href={fps}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ldd-lookup-fastpeoplesearch"
            title={`Buscar "${ownerName}" no FastPeopleSearch`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#10B981]/10 hover:bg-[#10B981] text-[#10B981] hover:text-white text-[11px] font-semibold transition"
          >
            <ExternalLink className="w-2.5 h-2.5" /> FastPeopleSearch
          </a>
        )}
        {biza && (
          <a
            href={biza}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ldd-lookup-bizapedia"
            title={`Buscar "${company}" no Bizapedia`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#F59E0B]/10 hover:bg-[#F59E0B] text-[#F59E0B] hover:text-white text-[11px] font-semibold transition"
          >
            <ExternalLink className="w-2.5 h-2.5" /> Bizapedia
          </a>
        )}
        {gmaps && (
          <a
            href={gmaps}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ldd-lookup-gmaps"
            title={`Buscar "${company}" no Google Maps`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#EA4335]/10 hover:bg-[#EA4335] text-[#EA4335] hover:text-white text-[11px] font-semibold transition"
          >
            <ExternalLink className="w-2.5 h-2.5" /> Google Maps
          </a>
        )}
      </div>
      <p className="text-[10.5px] text-[var(--al-text-muted)] leading-snug">
        Abre em nova aba já preenchido. Copie o telefone/e-mail encontrado
        e cole no campo <strong>Contato</strong> acima.
      </p>
    </div>
  );
}
